import { useRef, useState, type JSX } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import type { AddShoppingListItemRequest, ShoppingCategory } from "@recipevault/shared";
import { useSync } from "../../sync/useSync.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { useItemAutocomplete, type ItemSuggestion } from "./useItemAutocomplete.js";

interface AddItemRowProps {
  listId: string;
}

/**
 * Add-item box at the foot of a shopping list. A name input with a learned/seed
 * autocomplete dropdown, plus optional qty + unit. Selecting a suggestion prefills
 * the item's category (so it lands in the right group) and unit. Suggestions and
 * the submitted name are PLAIN TEXT only — never innerHTML.
 */
export function AddItemRow({ listId }: AddItemRowProps): JSX.Element {
  const { service } = useSync();

  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("");
  // Category is set only when a suggestion is chosen; otherwise undefined so the
  // backend categorizes from its own catalog/seed knowledge.
  const [category, setCategory] = useState<ShoppingCategory | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestions = useItemAutocomplete(name);
  const showDropdown = open && suggestions.length > 0;

  function reset(): void {
    setName("");
    setQty("");
    setUnit("");
    setCategory(undefined);
    setOpen(false);
  }

  function chooseSuggestion(s: ItemSuggestion): void {
    setName(s.displayName);
    setCategory(s.category);
    if (s.lastUnit) setUnit(s.lastUnit);
    setOpen(false);
    inputRef.current?.focus();
  }

  async function submit(): Promise<void> {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) return;

    const trimmedUnit = unit.trim();
    const parsedQty = qty.trim() === "" ? null : Number(qty);
    const body: AddShoppingListItemRequest = {
      name: trimmedName,
      ...(parsedQty != null && Number.isFinite(parsedQty) ? { qty: parsedQty } : {}),
      ...(trimmedUnit ? { unit: trimmedUnit } : {}),
      ...(category ? { category } : {}),
    };

    setSubmitting(true);
    try {
      await service.addItemOptimistic(listId, body);
      reset();
    } catch {
      toast.error("Couldn't add item — please try again");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex items-end gap-2"
      >
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Typing invalidates a previously picked suggestion's category.
              setCategory(undefined);
              setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
            }}
            onBlur={() => {
              // Delay so a suggestion mousedown registers before the list unmounts.
              window.setTimeout(() => {
                setOpen(false);
              }, 120);
            }}
            placeholder="Add an item…"
            aria-label="Add an item"
            maxLength={200}
            autoComplete="off"
          />

          {showDropdown && (
            <ul
              role="listbox"
              aria-label="Item suggestions"
              className="absolute bottom-full z-20 mb-1 max-h-56 w-full overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900 py-1 shadow-lg"
            >
              {suggestions.map((s) => (
                <li key={`${s.category}:${s.displayName}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    // onMouseDown (not onClick) so it fires before the input's onBlur.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      chooseSuggestion(s);
                    }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-zinc-100 hover:bg-zinc-800"
                  >
                    <span className="truncate">{s.displayName}</span>
                    <span className="shrink-0 text-xs text-zinc-500">{s.category}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Input
          value={qty}
          onChange={(e) => {
            setQty(e.target.value);
          }}
          placeholder="Qty"
          aria-label="Quantity"
          inputMode="decimal"
          className="w-16"
        />
        <Input
          value={unit}
          onChange={(e) => {
            setUnit(e.target.value);
          }}
          placeholder="Unit"
          aria-label="Unit"
          maxLength={32}
          className="w-20"
        />

        <Button type="submit" size="icon" disabled={!name.trim() || submitting} aria-label="Add item">
          <Plus className="h-4 w-4" aria-hidden />
        </Button>
      </form>
    </div>
  );
}
